import { View, Text, Platform } from "react-native";
import { GuidePageHeader, GuideGameCard, GuideTip, GuideNav, getGuideNav } from "./_components";

const PAGE_HREF = "/(web)/guide/games";

export default function GamesPage() {
  if (Platform.OS !== "web") return null;
  const nav = getGuideNav(PAGE_HREF);

  return (
    <View>
      <GuidePageHeader
        icon="game-controller"
        title="Spellen"
        subtitle="De app bevat 9 verschillende spellen. Speel solo, daag familieleden uit of speel als team!"
        color="#EF4444"
      />

      <Text className="text-base text-gray-600 leading-6 mb-6">
        Elk spel heeft zijn eigen regels, moeilijkheidsgraden en puntensysteem. Tijdens een actief 
        weekend verdien je punten voor je team. Buiten het weekend kun je "vrij spelen" om te oefenen.
      </Text>

      <GuideTip title="Solo vs Versus vs Team">
        Solo-spellen speel je alleen. Bij versus-spellen daag je een ander familielid uit voor 
        een één-op-één duel. Team-spellen speel je samen met je teamgenoten.
      </GuideTip>

      <View className="mt-8">
        <Text className="text-xl font-bold text-gray-900 mb-6">Alle Spellen</Text>

        <GuideGameCard
          icon="grid"
          name="Memory"
          description="Het klassieke kaartjesomdraaispel! Vind alle paren zo snel mogelijk. Er zijn drie moeilijkheidsgraden met verschillende rastergroottes."
          modes={["Solo", "Versus (tijdrace)"]}
          color="#8B5CF6"
          howToPlay="Tik op twee kaarten om ze om te draaien. Als ze hetzelfde plaatje tonen, heb je een paar gevonden en blijven ze open. Vind alle paren met zo min mogelijk zetten en zo snel mogelijk. Bij versus speelt ieder apart en wint degene met de beste tijd."
        />

        <GuideGameCard
          icon="help-circle"
          name="Quiz"
          description="Beantwoord meerkeuzevragen uit verschillende categorieën. Hoe sneller je antwoordt, hoe meer punten je verdient!"
          modes={["Solo"]}
          color="#3B82F6"
          howToPlay="Je krijgt een vraag te zien met 4 antwoordopties. Kies het juiste antwoord binnen de tijdslimiet. Hoe sneller je antwoordt, hoe meer punten je verdient. Er zijn vragen uit verschillende categorieën zoals natuur, sport, wetenschap en meer."
        />

        <GuideGameCard
          icon="tv"
          name="Live Quiz"
          description="Een Kahoot-achtige live quiz die door de quizmaster wordt gehost! Teams spelen samen in real-time."
          modes={["Team"]}
          color="#EF4444"
          howToPlay="De quizmaster start de quiz op een groot scherm (TV/projector). Teams kiezen een mascotte en de captain geeft de antwoorden. Vragen verschijnen tegelijk voor alle teams. Snelheid en correctheid bepalen de score. Tussen rondes door worden de tussenstand en ranglijst getoond."
        />

        <GuideGameCard
          icon="map"
          name="Speurtocht"
          description="Een scavenger hunt door het weekendgebied! Voltooi stappen door locaties te bezoeken, QR-codes te scannen en puzzels op te lossen."
          modes={["Team"]}
          color="#06B6D4"
          howToPlay="Volg de stappen van de speurtocht. Elke stap kan een locatie zijn die je moet bezoeken, een QR-code die je moet scannen, een foto die je moet maken, een puzzel die je moet oplossen of een woord dat je moet raden. Teams werken samen om alle stappen te voltooien."
        />

        <GuideGameCard
          icon="apps"
          name="Dammen"
          description="Het Nederlandse damspel op een 10x10 bord. Speel tegen de computer of daag een familielid uit!"
          modes={["Solo (vs AI)", "Versus"]}
          color="#10B981"
          howToPlay="Speel op een 10x10 bord volgens de Nederlandse damregels. Schijven bewegen diagonaal naar voren. Slaan is verplicht. Een schijf die de overkant bereikt wordt een dam en kan in alle diagonale richtingen bewegen. Wie alle stukken van de tegenstander slaat of blokkeert, wint."
        />

        <GuideGameCard
          icon="keypad"
          name="Sudoku"
          description="Los een Sudoku-puzzel op! Kies uit drie moeilijkheidsgraden en gebruik hints als je vastzit."
          modes={["Solo"]}
          color="#3B82F6"
          howToPlay="Vul het 9x9 raster zo in dat elke rij, kolom en 3x3 blok de cijfers 1 tot 9 precies één keer bevat. Tik op een leeg vakje en kies een cijfer. Gebruik hints als je vastzit. Hoe sneller je klaar bent en hoe minder hints je gebruikt, hoe hoger je score."
        />

        <GuideGameCard
          icon="diamond"
          name="Match 3"
          description="Wissel edelstenen om rijen van drie of meer te maken. Maak combo's voor extra punten en power-ups!"
          modes={["Solo"]}
          color="#EC4899"
          howToPlay="Wissel twee naast elkaar liggende edelstenen om een rij van 3 of meer gelijke stenen te vormen. Grotere combinaties en kettingreacties leveren meer punten op. Verzamel power-ups voor extra zetten en bonuspunten. Het spel heeft een tijdslimiet."
        />

        <GuideGameCard
          icon="walk"
          name="Endless Runner"
          description="Ren zo ver mogelijk als Opa of Oma! Spring over obstakels en verzamel munten."
          modes={["Solo"]}
          color="#F59E0B"
          howToPlay="Kies je karakter (Opa of Oma, elk met unieke abilities). Je karakter rent automatisch. Tik om te springen over obstakels. Verzamel munten voor bonuspunten. Hoe verder je komt, hoe hoger je score. Er is een leaderboard om je score te vergelijken."
        />

        <GuideGameCard
          icon="close"
          name="Boter Kaas en Eieren"
          description="Het klassieke spel in een serie! Speel best-of-3 of best-of-5 tegen de computer of een familielid."
          modes={["Solo (vs AI)", "Versus"]}
          color="#6366F1"
          howToPlay="Zet je symbool (X of O) op het 3x3 bord. Wie als eerste drie op een rij heeft (horizontaal, verticaal of diagonaal) wint de ronde. Bij een serie speel je meerdere rondes en wint degene die als eerste de meerderheid wint."
        />
      </View>

      {/* Versus uitdagingen */}
      <Text className="text-xl font-bold text-gray-900 mb-4 mt-8">Versus Uitdagingen</Text>
      <Text className="text-base text-gray-600 leading-6 mb-6">
        Bij spellen die versus-modus ondersteunen kun je een ander familielid uitdagen. 
        De uitgenodigde persoon ontvangt een melding en kan de uitdaging accepteren. 
        Het spel wordt in real-time gesynchroniseerd zodat jullie tegelijk spelen.
      </Text>

      <GuideTip variant="warning" icon="time" title="Uitdaging verlopen">
        Een uitdaging verloopt als de ander niet binnen een bepaalde tijd reageert. 
        Je kunt daarna een nieuwe uitdaging sturen.
      </GuideTip>

      {/* Punten */}
      <Text className="text-xl font-bold text-gray-900 mb-4 mt-8">Punten per Spel</Text>
      <Text className="text-base text-gray-600 leading-6 mb-6">
        Tijdens een actief weekend verdien je punten voor je team. De commissie stelt per 
        spel in hoeveel punten een overwinning, deelname of bonusprestatie waard is. 
        Bij vrij spelen worden scores apart bijgehouden zonder teampunten.
      </Text>

      <GuideTip variant="success" icon="ribbon" title="Vrij spelen">
        Ook buiten het weekend kun je alle spellen spelen! Je scores worden bijgehouden 
        op een apart "vrij spelen" leaderboard.
      </GuideTip>

      <GuideNav {...nav} />
    </View>
  );
}
